import * as _ from "lodash";
import * as React from "react";
import { useDispatch } from "redux-react-hook";
import styled from "styled-components";

import { Button } from "src/library/components/Button";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import Grid from "src/library/components/Hedron";
import ProductCategorySelect from "src/core/products/components/ProductCategories/ProductCategorySelect";
import ProductInfoSidebar from "src/core/products/components/ProductInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  useProductsApi,
  IProductCategory
} from "src/core/products/api/products.api";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { useProduct } from "src/core/products/hooks";
import { PageProps } from "src/pages/Router/routes";
import { useCurrentUser } from "src/core/users/hooks";

interface UrlParams {
  productId: string;
}

/**
 * Main "Product Categories" View
 *
 * @returns
 */
const ProductCategoriesPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const {
    match, history, terminology
  } = props;

  const productId = match ? parseInt(match.params.productId, 0) : 0;

  const {
    isLoading: isLoadingProduct, product, saveProduct, isSavingProduct
  } = useProduct(productId);

  const [ listCategoriesResponse, getProductCategories ] = useProductsApi("LIST_PRODUCT_CATEGORIES");
  const [ formattedCategories, setFormattedCategories ] = React.useState<Array<IProductCategory & { subCategories?: IProductCategory[] }>>([]);
  const [ selectedCategories, setSelectedCategories ] = React.useState<number[]>([]);
  const productCategoriesData = listCategoriesResponse.data;
  const [ { currentRole } ] = useCurrentUser();
  const dispatch = useDispatch();

  React.useEffect(() => {
    getProductCategories({
      filters: {},
      branchId: null
    });
  }, [ getProductCategories ]);

  React.useEffect(() => {
    if (listCategoriesResponse.data) {
      const parentCategories: Array<IProductCategory & { subCategories?: IProductCategory[] }> = _.filter(
        listCategoriesResponse.data,
        category => category.parentId === null
      );

      _.each(parentCategories, (parent, index) => {
        if (listCategoriesResponse.data) {
          parentCategories[ index ].subCategories = _.filter(listCategoriesResponse.data, category => category.parentId === parent.id);
        }
      });
      setFormattedCategories(parentCategories);
    }

    if (listCategoriesResponse.error) {
      dispatch(setGlobalError(listCategoriesResponse.error));
    }

    if (product) {
      setSelectedCategories(_.map(product.categories, cat => cat.id));
    }
  }, [ listCategoriesResponse ]);

  React.useEffect(() => {
    if (formattedCategories && product) {
      setSelectedCategories(_.map(product.categories, cat => cat.id));
    }
  }, [ product ]);

  const onCategoryChange = (categoryId: number) => {
    if (selectedCategories.includes(categoryId)) {
      setSelectedCategories(_.filter(selectedCategories, cat => cat !== categoryId));
    } else {
      setSelectedCategories([ ...selectedCategories, categoryId ]);
    }
  };

  const handleSave = () => {
    if (product) {
      saveProduct({
        id: product.id,
        categories: productCategoriesData ? _.filter(productCategoriesData, cat => selectedCategories.includes(cat.id)) : []
      });
    }
  };

  return (
    <Layout
      {...props}
      title={product ? `${product.description} - Categories` : ""}
    >
      <MainContentWrapper fill>
        {listCategoriesResponse.pending || isLoadingProduct ? (
          <LoadingSpinner
            type="circular"
            position="inline"
          />
        ) : (
          _.map(formattedCategories, parent => (
            <ParentCategory
              key={parent.id}
              direction="vertical"
            >
              <ProductCategorySelect
                category={parent}
                selected={selectedCategories}
                onSelect={onCategoryChange}
              />
            </ParentCategory>
          ))
        )}
      </MainContentWrapper>

      <InfoBarWrapper>
        <ProductInfoSidebar
          terminology={terminology}
          product={product}
          isLoading={isLoadingProduct}
          isAdmin={currentRole?.type === "admin"}
          primaryAction={(
            <Button
              disabled={isSavingProduct}
              $buttonType="primary"
              background="lightGrey"
              onClick={() => history.goBack()}
            >
              Cancel
            </Button>
          )}
          secondaryAction={(
            <Button
              $buttonType="submit"
              type="submit"
              loading={isSavingProduct}
              background="primaryDark"
              onClick={() => handleSave()}
            >
              Save Categories
            </Button>
          )}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

const ParentCategory = styled(Grid.Bounds)``;

export default ProductCategoriesPage;
